import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";

import { analyticsStoreEvent } from "@/mk2/actions";
import CounsellingsHome from "@/mk2/apps/counsellings/containers/CounsellingsHome/CounsellingsHome";
import { counsellingsHomeLoad } from "@/mk2/apps/counsellings/containers/CounsellingsHome/CounsellingsHome.actions";
import counsellingsHomePageReducer, {
    CounsellingsHomePageState,
    CounsellingsHomePostsState,
    TopicStats,
} from "@/mk2/apps/counsellings/containers/CounsellingsHome/CounsellingsHome.reducers";
import saga from "@/mk2/apps/counsellings/containers/CounsellingsHome/CounsellingsHome.sagas";
import {
    getCounsellingsHomeComplaintsStatisticsPerTopic,
    getCounsellingsHomePageState,
    getCounsellingsHomePostsState,
} from "@/mk2/apps/counsellings/containers/CounsellingsHome/CounsellingsHome.selectors";
import { counsellingsHomeUrl } from "@/mk2/apps/counsellings/urls";
import { getForumComplaintsStatistics, getForumNotificationsOn } from "@/mk2/apps/forum/reducers";
import {
    ComplaintsCountPerTopic,
    ComplaintsStatisticsPerPost,
    CounsellingEntity,
    CounsellingSchema,
    PostEntity,
    PostSchema,
    TopicEntity,
    TopicSchema,
} from "@/mk2/apps/forum/schemas";
import { breadcrumbsCounsellings } from "@/mk2/apps/forum/structuredData";
import { memoizeOne } from "@/mk2/helpers/cache";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, { BrandingProps, HalfpageProps, InterstitialProps } from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { UserEntity } from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_DESCRIPTION_COUNSELLINGS,
    SEO_TITLE_COUNSELLINGS,
} from "@/mk/autogenerated/translation";

interface OwnProps {}

interface StateProps {
    after: number;
    posts: PostEntity[];
    hasNext: number;
    hasPrevious: number;
    isMobile: boolean;
    notificationsOn: number[];
    requestUser: UserEntity;
    loadingState: LoadingState;
    errorMessage: string;
    baseUrl: string;
    permissions: string[];
    complaintsStatistics: ComplaintsStatisticsPerPost;
    complaintsStatisticsPerTopic: ComplaintsCountPerTopic;
    counsellings: CounsellingEntity[];
    topicStats: TopicStats;
    lastVisitCounts: TopicStats;
    followingTopics: TopicEntity[];
    totalAnswers: number;
    resultsCount: number;
    query: string;
}

interface DispatchProps {
    onLoad(query: string, after?: number);
    onStoreEvent(name: string, props: any);
}

type RouteProps = RouteComponentProps<{}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

interface Query {
    after: number;
    query: string;
}

export default class CounsellingsHomePage extends Page<OwnProps, StateProps, DispatchProps> {
    private parseQuery = memoizeOne((qs: string): Query => {
        const qp = parse(qs, { parseNumbers: true }) || {};
        return {
            after: qp.after as number,
            query: (qp.q as string) || "",
        };
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsedQuery: Query = this.parseQuery(ownProps.location.search);
            const after = parsedQuery.after;
            const query = parsedQuery.query;

            const pageState: CounsellingsHomePageState = getCounsellingsHomePageState(state);
            const postsState: CounsellingsHomePostsState = getCounsellingsHomePostsState(
                state,
                query,
            );

            return {
                after,
                posts: getDenormalizedEntities<PostEntity>(state, PostSchema, postsState.ids),
                hasNext: postsState.hasNext,
                hasPrevious: postsState.hasPrevious,
                loadingState: postsState.loadingState,
                errorMessage: postsState.errorMessage,
                notificationsOn: getForumNotificationsOn(state),
                isMobile: getRequestDeviceMobile(state),
                requestUser: getRequestUser(state),
                baseUrl: getRequestBaseUrl(state),
                permissions: getRequestPermissions(state),
                complaintsStatistics: getForumComplaintsStatistics(state),
                complaintsStatisticsPerTopic: getCounsellingsHomeComplaintsStatisticsPerTopic(
                    state,
                    query,
                ),
                counsellings: getDenormalizedEntities<CounsellingEntity>(
                    state,
                    CounsellingSchema,
                    pageState.counsellingIds,
                ),
                followingTopics: getDenormalizedEntities<TopicEntity>(
                    state,
                    TopicSchema,
                    pageState.followingTopicIds,
                ),
                topicStats: pageState.topicStats,
                lastVisitCounts: pageState.lastVisitCounts,
                totalAnswers: pageState.totalAnswers,
                resultsCount: postsState.resultsCount,
                query,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: counsellingsHomeLoad,
            onStoreEvent: analyticsStoreEvent,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_COUNSELLINGS;
    }

    public getMetaDescription(props: Props): string {
        return SEO_DESCRIPTION_COUNSELLINGS;
    }

    public getStructuredData(props: Props): object[] {
        return [breadcrumbsCounsellings(props.baseUrl)];
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        return baseUrl + counsellingsHomeUrl;
    }

    public render(props: Props): false | JSX.Element {
        return (
            <span>
                <CounsellingsHome {...props} />
            </span>
        );
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Forum Other - Halfpage Sticky (Desktop)",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        const { isMobile } = props;
        return isMobile
            ? null
            : {
                  zone: "Forum Other - Branding",
              };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Forum Other - Interstitial" };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.counsellings.home", counsellingsHomePageReducer);
        store.injectSaga("containers.counsellings.home", saga);
    }
}
